import { getToken } from "@/utils/rezeptAPI/getToken";
import { getAllRecipes } from "@/utils/rezeptAPI/getAllRecipes";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import {
  EquipmentType,
  ImageType,
  MenuType,
  RecipeType,
  StepZutatList,
} from "@/types/baseTypes";
import { getRecipeSingleViewProps } from "@/utils/rezeptAPI/getRecipeSingleViewProps";
import { Post } from "@/wp/types/client";
import { Footer } from "@/components/Footer/Footer";
import GlossarTooltip from "@/components/GlossarTooltip/GlossarTooltip";
import { HeadMeta } from "@/components/Header/Head";
import { Navbar, NavbarOptionType } from "@/components/Navbar/Navbar";
import { GlobalContextProvider } from "@aleksundshantu.com/aunds-utils-react/contexts";
import {
  LabelsType,
  OptionsFooterType,
  OptionsRecipeType,
  OptionsSearchType,
  OptionsSecurityHeader,
  OptionsSocialMediaType,
  OptionsTopfiGraphicsType,
} from "@/types/OptionType";
import { IconsType } from "@/types/ModuleOptionsType";
import RecipeSingleView from "@/components/RecipeComponents/RecipeSingleView/RecipeSingleView";
import LoginAreaFormWrapper from "@/components/Header/ServiceApiComponents/SharedComponents/LoginAreaFormWrapper/LoginAreaFormWrapper";
import { optimizeURL } from "@/utils/url.ts";
import { RatingProvider } from "@/context/RatingContext.tsx";
import { invalidateReviewCache } from "@/utils/star-cache.ts";
/* jscpd:ignore-start */
export interface RecipePageProps {
  recipeDetails: RecipeType;
  labels: LabelsType;
  mainMenu: MenuType;
  footerMenu: MenuType;
  pageData: Post;
  icons: IconsType;
  searchOptions: OptionsSearchType;
  socialMedia: OptionsSocialMediaType;
  footerOptions: OptionsFooterType;
  fallbackShareImage: ImageType | false;
  securityHeaderOptions: OptionsSecurityHeader;
  topfiGraphics: OptionsTopfiGraphicsType;
  navbarOption: NavbarOptionType;
  legalMenu: MenuType;
  favCount: number;
  ingredientsList: StepZutatList;
  isCookingMode?: boolean;
  recipeOptions: OptionsRecipeType;
  equipmentList: EquipmentType[];
}

export default function Page({
  recipeDetails,
  mainMenu,
  footerMenu,
  pageData,
  searchOptions,
  socialMedia,
  labels,
  icons,
  footerOptions,
  fallbackShareImage,
  securityHeaderOptions,
  topfiGraphics,
  navbarOption,
  legalMenu,
  favCount,
  ingredientsList,
  isCookingMode = false,
  recipeOptions,
  equipmentList,
}: RecipePageProps) {
  const mainstartAriaLabel = labels?.find(
    (label) => label.key === "aria_label_mainstart",
  );
  const mainstartLabel = labels?.find((label) => label.key === "skip_nav");
  return (
    <GlobalContextProvider iconsRaw={icons} labelsRaw={labels}>
      <HeadMeta
        post={pageData}
        fallbackShareImage={fallbackShareImage}
        securityHeaderOptions={securityHeaderOptions}
      />
      <div id="pagestart" tabIndex={-1} />
      {/* eslint-disable-next-line react/forbid-elements */}
      <a
        className="skipnav"
        href="#maincontent"
        {...(mainstartAriaLabel && {
          "aria-label": mainstartAriaLabel.value,
        })}
      >
        {mainstartLabel ? mainstartLabel.value : "Navigation überspringen"}
      </a>
      {!isCookingMode && (
        <Navbar
          searchPageSlug={
            searchOptions?.search_page_link
              ? optimizeURL(searchOptions?.search_page_link) || ""
              : ""
          }
          menu={mainMenu}
          socialMedia={socialMedia.platforms}
          navbarOption={navbarOption}
          pageData={pageData}
          isHome={false}
        />
      )}

      {pageData && (
        <>
          {!isCookingMode && <div id="maincontent" />}
          <main key={pageData.slug} id={`main-tag`}>
            <LoginAreaFormWrapper
              recipeDetails={recipeDetails}
              equipmentList={equipmentList}
            >
              <RatingProvider
                recipeId={recipeDetails.rezept_id}
                initialStars={recipeDetails.bewertung_stars}
                initialCount={recipeDetails.bewertung_anzahl}
                invalidateReviewCache={invalidateReviewCache}
              >
                <RecipeSingleView
                  recipeDetails={recipeDetails}
                  favCount={favCount}
                  topfiGraphics={topfiGraphics}
                  ingredientsList={ingredientsList}
                  isCookingMode={isCookingMode}
                  recipeOptions={recipeOptions}
                  equipmentList={equipmentList}
                />
              </RatingProvider>
            </LoginAreaFormWrapper>
          </main>
        </>
      )}
      <Footer
        autoShowConsentManagement={
          "header_seite" in pageData.acf
            ? pageData.acf?.header_seite?.show_consent_management
            : undefined
        }
        footerOptions={footerOptions}
        socialMedia={socialMedia}
        footerMenu={footerMenu}
        legalMenu={legalMenu}
      />
      <GlossarTooltip key={pageData.slug} />
    </GlobalContextProvider>
  );
}
/* jscpd:ignore-end */
export const getStaticPaths: GetStaticPaths = async () => {
  // Explicitly type the paths array for clarity and type safety.
  // For [id].tsx, the 'id' parameter in 'params' must be a string.
  const paths: { params: { id: string[] } }[] = [];

  let token: string = "";
  try {
    token = await getToken();
  } catch (error) {
    console.error("Error fetching token in getStaticPaths:", error);
  }

  // Ensure getAllRecipes correctly returns an object with a 'rezepte' array property
  const allRecipes = await getAllRecipes(token, "de", "de");
  const filteredRecipes = allRecipes?.rezepte.filter(
    (recipe) => recipe.produkttyp === "Der kleine Tiptopf",
  );

  // Check the environment variable. It should be defined in next.config.js or directly in your build environment.
  if (process.env.BUILD_ALL_POST === "true" && allRecipes && filteredRecipes) {
    // Use forEach for cleaner iteration when not returning a new array
    filteredRecipes.forEach((recipe: RecipeType) => {
      const recipeId = String(recipe.rezept_id); // Ensure ID is a string

      // Path for /rezepte/[id] (e.g., /rezepte/pizza)
      paths.push({
        params: {
          id: [recipeId], // slug is an array with just the ID
        },
      });
      paths.push({
        params: {
          id: [recipeId, "kochmodus"], // slug is an array with just the ID
        },
      });
    });
  }

  return {
    paths,
    fallback: "blocking", // Or 'true' or 'false' depending on your strategy
  };
};

export async function getStaticProps(context: GetStaticPropsContext) {
  if (context.locale !== "de") {
    return {
      notFound: true,
      revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
    };
  }
  return await getRecipeSingleViewProps(context);
}
